import {
  DEFAULT_DURATION_IN_MINUTES,
  MAXIMUM_DATE_RANGE_IN_MONTHS,
  TIMESLOT_BUFFER_IN_HOURS
} from '@/utils/constants';
import { DentistHour, Request } from '@/utils/schema';
import moment, { Moment } from 'moment';

export type BlockingRequest = Pick<Request, 'date' | 'start_time' | 'end_time'>;

/**
 * One row per weekday. The last row wins, so a later edit replaces an older
 * start time, and a later closed row removes that day.
 */
export const collapseDentistHours = (hours: DentistHour[]): DentistHour[] => {
  const byDay = new Map<number, DentistHour>();

  hours.forEach((hour) => {
    const day = Number(hour.day);
    if (Number.isNaN(day)) return;
    byDay.set(day, { ...hour, day });
  });

  return [...byDay.values()].filter((hour) => hour.open);
};

export const parseAppointmentTime = (
  date: string,
  time: string | null | undefined
): Moment => moment(`${date} ${time ?? ''}`, 'YYYY-MM-DD HH:mm');

export const rangesOverlap = (
  aStart: Moment,
  aEnd: Moment,
  bStart: Moment,
  bEnd: Moment
): boolean => {
  if (
    !aStart.isValid() ||
    !aEnd.isValid() ||
    !bStart.isValid() ||
    !bEnd.isValid()
  ) {
    return false;
  }

  return (
    aStart.isBetween(bStart, bEnd, null, '[)') ||
    aEnd.isBetween(bStart, bEnd, null, '(]') ||
    bStart.isBetween(aStart, aEnd, null, '[)') ||
    bEnd.isBetween(aStart, aEnd, null, '(]')
  );
};

export const findDayHours = (
  hours: DentistHour[],
  date: string
): DentistHour | undefined => {
  const dayOfWeek = moment(date, 'YYYY-MM-DD').day();
  return hours.find((hour) => Number(hour.day) === dayOfWeek && hour.open);
};

export const isWithinWorkingHours = (
  date: string,
  startTime: string,
  endTime: string,
  dayHours: DentistHour | undefined
): boolean => {
  if (!dayHours?.open || !dayHours.start_time || !dayHours.end_time) {
    return false;
  }

  const slotStart = parseAppointmentTime(date, startTime);
  const slotEnd = parseAppointmentTime(date, endTime);
  const dayStart = parseAppointmentTime(date, dayHours.start_time);
  const dayEnd = parseAppointmentTime(date, dayHours.end_time);

  return (
    slotStart.isValid() &&
    slotEnd.isValid() &&
    dayStart.isValid() &&
    dayEnd.isValid() &&
    slotStart.isSameOrAfter(dayStart) &&
    slotEnd.isAfter(slotStart) &&
    slotEnd.isSameOrBefore(dayEnd)
  );
};

export const conflictsWithRequests = (
  date: string,
  startTime: string,
  endTime: string,
  requests: BlockingRequest[]
): boolean => {
  const slotStart = parseAppointmentTime(date, startTime);
  const slotEnd = parseAppointmentTime(date, endTime);
  const day = moment(date, 'YYYY-MM-DD');

  return requests.some((request) => {
    if (
      !request.date ||
      !day.isSame(moment(request.date, 'YYYY-MM-DD'), 'day')
    ) {
      return false;
    }

    return rangesOverlap(
      slotStart,
      slotEnd,
      parseAppointmentTime(request.date, request.start_time),
      parseAppointmentTime(request.date, request.end_time)
    );
  });
};

export const getOpenTimeslots = ({
  date,
  dentistHours,
  requests,
  durationInMinutes,
  now = moment()
}: {
  date: Date;
  dentistHours: DentistHour[];
  requests: BlockingRequest[] | null | undefined;
  durationInMinutes: number;
  now?: Moment;
}): string[] => {
  if (!requests) return [];

  const dateText = moment(date).format('YYYY-MM-DD');
  const dentistDayHours = findDayHours(dentistHours, dateText);

  if (!dentistDayHours?.start_time || !dentistDayHours.end_time) {
    return [];
  }

  const startTime = parseAppointmentTime(dateText, dentistDayHours.start_time);
  const endTime = parseAppointmentTime(dateText, dentistDayHours.end_time);

  if (
    !startTime.isValid() ||
    !endTime.isValid() ||
    !startTime.isBefore(endTime)
  ) {
    return [];
  }

  const timeslots: string[] = [];
  let currentTime = startTime.clone();
  const bufferTime = now.clone().add(TIMESLOT_BUFFER_IN_HOURS, 'hours');
  const duration = durationInMinutes || DEFAULT_DURATION_IN_MINUTES;
  let guard = 0;

  while (currentTime.isBefore(endTime) && guard < 200) {
    guard += 1;

    if (currentTime.isBefore(startTime)) {
      currentTime = startTime.clone();
    }

    const slotEndTime = currentTime.clone().add(duration, 'minutes');
    const conflict = requests.find((request) => {
      if (
        !request.date ||
        !moment(date).isSame(moment(request.date, 'YYYY-MM-DD'), 'day')
      ) {
        return false;
      }

      return rangesOverlap(
        currentTime,
        slotEndTime,
        parseAppointmentTime(request.date, request.start_time),
        parseAppointmentTime(request.date, request.end_time)
      );
    });

    if (
      !conflict &&
      currentTime.isSameOrAfter(startTime) &&
      currentTime.isAfter(bufferTime) &&
      slotEndTime.isSameOrBefore(endTime)
    ) {
      timeslots.push(currentTime.format('HH:mm'));
    } else if (conflict?.date && conflict.end_time) {
      const jumped = parseAppointmentTime(conflict.date, conflict.end_time);
      if (jumped.isAfter(currentTime)) {
        currentTime = jumped.isBefore(startTime) ? startTime.clone() : jumped;
        continue;
      }
    }

    const remainder =
      DEFAULT_DURATION_IN_MINUTES -
      (currentTime.minute() % DEFAULT_DURATION_IN_MINUTES);

    currentTime.add(
      remainder > 0 ? remainder : DEFAULT_DURATION_IN_MINUTES,
      'minute'
    );
  }

  return timeslots;
};

export const getAvailableDates = ({
  dentistHours,
  requests,
  durationInMinutes,
  now = moment()
}: {
  dentistHours: DentistHour[];
  requests: BlockingRequest[] | null | undefined;
  durationInMinutes: number;
  now?: Moment;
}): Date[] => {
  if (!requests) return [];

  const maximumDate = now
    .clone()
    .add(MAXIMUM_DATE_RANGE_IN_MONTHS, 'months')
    .endOf('week');
  const dates: Date[] = [];
  const date = now.clone();

  while (date.isBefore(maximumDate)) {
    const timeslots = getOpenTimeslots({
      date: date.toDate(),
      dentistHours,
      requests,
      durationInMinutes,
      now
    });

    if (timeslots.length > 0) {
      dates.push(date.toDate());
    }

    date.add(1, 'day');
  }

  return dates;
};
